import BuildCoin from "../images/buildcoin.png";

const formatPrice = (n) => Number(n || 0).toLocaleString("en-IN");

const RFQItemsView = ({ itemsExpanded, items, rfqData, handleSwap }) => {
  if (!itemsExpanded) return null;

  // Group items by product category name
  const grouped = items.reduce((acc, item) => {
    const categoryObj = rfqData.product_categories.find(
      (cat) => cat._id === item.product_category_id
    );

    const categoryName = categoryObj
      ? categoryObj.product_category_name
      : "Other Products";

    if (!acc[categoryName]) acc[categoryName] = [];
    acc[categoryName].push(item);

    return acc;
  }, {});

  return (
    <div className="mt-4">
      {Object.entries(grouped).map(([categoryName, categoryItems], cIdx) => (
        <div key={cIdx} className="mb-4">
          {/* Category Title */}
          <h3 className="text-sm font-semibold text-gray-700 mb-2 px-4">
            {categoryName}
          </h3>

          {/* ITEMS */}
          {categoryItems.map((item, idx) => {
            const quantity = Number(item.quantity || 1);

            // ⭐ ALL TOTALS + PRICES FROM API (no UI calculation)
            const unitMRP = Number(item.price_before_savings || item.selling_price || 0);
            const unitFinal = Number(item.price_after_savings || item.selling_price || 0);
            const item_actual_price = item.item_actual_price || 0
            const totalMRP = Number(item.estimated_item_total) /* Number(item.item_total_before_savings || unitMRP * quantity) */;
            const item_sub_total = Number(item.estimated_item_sub_total);
            const totalFinal = Number(item.item_total_after_savings || unitFinal * quantity - (item.saving_build_coins || 0));

            const savingsCoins = Number(item.saving_build_coins || 0);
            const isNotAvailable = item.not_available;

            return (
              <div
                key={item.item_id ?? idx}
                className={`px-4 py-3 relative ${
                  idx !== categoryItems.length - 1
                    ? "border-b border-gray-100"
                    : ""
                }`}
              >
                {/* MAIN ITEM ROW */}
                <div
                  className={`flex items-center justify-between relative z-0 ${
                    isNotAvailable
                      ? "bg-gradient-to-r from-gray-50 to-gray-100 opacity-60 rounded-md p-2"
                      : ""
                  }`}
                >
                  {/* LEFT SIDE */}
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    {/* Image */}
                    <div className="w-12 h-12 bg-gray-50 border border-gray-200 rounded-md overflow-hidden flex items-center justify-center">
                      {item.item_image ? (
                        <img
                          src={`${process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES}${item.item_image}`}
                          alt={item.item_name}
                          className="object-contain w-full h-full"
                          onError={(e) => {
                            e.target.style.display = "none";
                            e.target.parentNode.innerHTML =
                              '<span class="text-gray-400 text-[10px] text-center">No Image</span>';
                          }}
                        />
                      ) : (
                        <span className="text-gray-400 text-[10px] text-center">
                          No Image
                        </span>
                      )}
                    </div>

                    {/* Details */}
                    <div className="min-w-0">
                      {/* Product Name */}
                      <div className="text-sm font-medium text-gray-900 line-clamp-2">
                        {item.item_name}
                      </div>

                      {/* Price × Qty */}
                      <div className="text-[12px] text-gray-500 mt-1 flex items-center gap-1">
                        {item_actual_price > 0 && item_actual_price !== unitMRP ?(
                          <span className="line-through mr-1">
                          ₹{item_actual_price} 
                        </span>
                        ):(
                          <>
                          </>
                        )}
                        <span>
                          ₹{formatPrice(unitMRP)}
                        </span>
                        <span className="mx-1 text-gray-400">×</span>
                        {quantity} {item?.UOM || "qty"}
                      </div>
                    </div>
                  </div>

                  {/* RIGHT SIDE - TOTAL + COINS SAVED */}
                  <div className="flex flex-col items-end min-w-[80px]">
                    {/* Original total (strike) */}
                    { item_sub_total!== totalFinal && (
                      <div className="text-xs text-gray-400 line-through">
                        ₹{formatPrice(item_sub_total)}
                      </div>
                    )}

                    {/* FINAL TOTAL WITH COINS SAVINGS */}
                    <div className="flex items-center gap-1">
                      <div className="text-lg font-semibold text-gray-900 tracking-tight">
                        ₹{formatPrice(totalFinal)}
                      </div>

                      {savingsCoins > 0 && (
                        <div className="flex items-center ml-1">
                          <span className="text-green-600 font-semibold text-sm">
                            +{savingsCoins}
                          </span>
                          <img
                            src={BuildCoin}
                            className="h-4 w-4 ml-1"
                            alt="coin"
                          />
                        </div>
                      )}
                    </div>

                    {/* Out of Stock */}
                    {isNotAvailable && (
                      <div className="text-xs font-medium text-red-600 mt-1">
                        Out of Stock
                      </div>
                    )}
                  </div>
                </div>

                {/* SUGGESTED ITEMS */}
                {item.suggested_items &&
                  item.suggested_items.length > 0 && (
                    <div className="mt-3">
                      <div className="text-xs text-gray-500 mb-2 font-medium flex items-center gap-1">
                        <span>💡 Other Options:</span>
                      </div>

                      <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                        {item.suggested_items.map((sug, sIdx) => (
                          <div
                            key={sIdx}
                            className="min-w-[160px] max-w-[180px] border-2 border-gray-300 rounded-lg p-2 bg-gradient-to-br from-gray-50 to-white hover:shadow-md hover:border-gray-400 transition-all duration-300 flex-shrink-0 cursor-pointer"
                          >
                            <div className="flex items-center gap-2">
                              <div className="w-10 h-10 rounded-md flex items-center justify-center overflow-hidden border border-gray-200 flex-shrink-0">
                                {sug.item_image ? (
                                  <img
                                    src={`${process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES}${sug.item_image}`}
                                    alt={sug.item_name}
                                    className="object-contain w-full h-full"
                                  />
                                ) : (
                                  <span className="text-gray-400 text-[8px]">
                                    No Image
                                  </span>
                                )}
                              </div>

                              <div className="flex-1 min-w-0">
                                <div className="text-[10px] font-medium text-gray-800 line-clamp-2 leading-tight">
                                  {sug.item_name}
                                </div>

                                <div className="flex items-center space-x-4 mt-1">
                                  <div className="text-[10px] text-gray-800">
                                    {sug.quantity} {sug.UOM && `(${sug.UOM})`}
                                  </div>

                                  <div className="text-xs font-bold text-green-700">
                                    ₹{formatPrice(sug.selling_price * sug.quantity)}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Swap Button */}
                            <button
                              onClick={() => handleSwap(item, sug)}
                              className="mt-2 w-full bg-gradient-to-r from-red-500 to-red-600 text-white text-[9px] py-1 rounded-md hover:from-red-600 hover:to-red-700 transition-colors duration-200 font-medium flex items-center justify-center gap-1"
                            >
                              <span>🔄</span> SWAP
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

export default RFQItemsView;
